'use client'

import { Locale, useLocale } from 'next-intl'
import { useParams, useSearchParams } from 'next/navigation'
import Script from 'next/script'
import {
  PropsWithChildren,
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { VSC_SDK_LOADED_EVENT } from '@/constants/vsc-sdk'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useEnableFull3d } from '@/hooks/useEnableFull3d'
import { usePathname, useRouter } from '@/i18n/navigation'
import {
  enableDevShareComponent,
  getCreateDomain,
  getShareComponentSDKUri,
  getWorldsDomain,
} from '@/lib/configs'
import { setUserLocale } from '@/server/services/locales'

type VSCSdkContextType = {
  viverseShareComponent: ViverseShareComponent | null
  isMountedHeader: boolean
  setIsMountedHeader: (mounted: boolean) => void
  changeLocale: (locale: Locale) => Promise<void>
}

const VSCSdkContext = createContext<VSCSdkContextType | null>(null)

const useCreateShareComponentLinks = () => {
  const { appConfig } = useConfigsContext()
  const { viverse_landing, viverse_market, viverse_avatar } = appConfig?.links || {}
  const worldsDomain = enableDevShareComponent() ? location.origin : `https://${getWorldsDomain()}`
  const createDomain = enableDevShareComponent() ? location.origin : `https://${getCreateDomain()}`
  const viverseDomain = viverse_landing?.replace(/\/$/, '')
  const marketDomain = viverse_market?.replace(/\/$/, '')
  const avatarDomain = viverse_avatar?.replace(/\/$/, '')

  const shareComponentLinks = useMemo(() => {
    return {
      viverse: viverseDomain!,
      world: worldsDomain,
      market: marketDomain!,
      avatar: avatarDomain!,
      create: createDomain,
    }
  }, [viverseDomain, worldsDomain, marketDomain, avatarDomain, createDomain])

  return shareComponentLinks
}

export const VSCSdkProvider = ({ children }: PropsWithChildren) => {
  const sdkUri = getShareComponentSDKUri()

  const lang = useLocale()
  const router = useRouter()
  const params = useParams()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const shareComponentLinks = useCreateShareComponentLinks()
  const enableFull3d = useEnableFull3d()

  const [isReady, setIsReady] = useState(false)
  const [isMountedHeader, setIsMountedHeader] = useState(false)
  const [viverseShareComponent, setViverseShareComponent] = useState<ViverseShareComponent | null>(
    null,
  )

  const handleLoad = () => {
    console.log('✅ VSCSdk loaded')
    window.dispatchEvent(new CustomEvent(VSC_SDK_LOADED_EVENT))
  }

  const handleReady = () => {
    console.log('🔥 VSCSdk ready')
    setIsReady(true)
  }

  const handleError = (e: Error) => {
    console.error('VSCSdk script failed to load', e)
  }

  const changeLocale = useCallback(
    async (locale: Locale) => {
      await setUserLocale(locale)

      const query = Object.fromEntries(searchParams)

      startTransition(() => {
        router.replace(
          // @ts-expect-error -- TypeScript will validate that only known `params`
          // are used in combination with a given `pathname`. Since the two will
          // always match for the current route, we can skip runtime checks.
          { pathname, params, query },
          { locale },
        )
      })
    },
    [params, pathname, router, searchParams],
  )

  useEffect(() => {
    if (typeof ViverseShareComponent !== 'undefined' && isReady) {
      setViverseShareComponent(
        new ViverseShareComponent({
          i18n: {
            lang,
          },
          origin: shareComponentLinks,
        }),
      )
    }
  }, [lang, shareComponentLinks, isReady])

  return (
    <VSCSdkContext.Provider
      value={{
        viverseShareComponent,
        isMountedHeader,
        setIsMountedHeader,
        changeLocale,
      }}
    >
      {children}
      {sdkUri && !enableFull3d && (
        <Script src={sdkUri} onLoad={handleLoad} onError={handleError} onReady={handleReady} />
      )}
    </VSCSdkContext.Provider>
  )
}

export const useVSCSdkContext = () => {
  const value = useContext(VSCSdkContext)

  if (value == null) {
    throw new Error('useVSCSdkContext cannot be used outside of VSCSdkProvider')
  }

  return value
}
